import React, { useState, useEffect } from 'react';
import { Linkedin } from 'lucide-react';
import XIcon from '../components/icons/XIcon';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    reason: 'general',
    organisation: '',
    quantity: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [socialLinks, setSocialLinks] = useState({ linkedin: '', twitter: '' });

  // Scroll to top when component mounts + load social links
  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social-links`)
      .then((r) => r.json())
      .then((d) => setSocialLinks({ linkedin: d.linkedin || '', twitter: d.twitter || '' }))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          reason: 'general',
          organisation: '',
          quantity: '',
          message: ''
        });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect. Please try again later.');
      console.error('Contact form error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section
        style={{
          background: '#FFFFFF',
          padding: '120px 40px 80px'
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '500',
              marginBottom: '24px',
              color: '#111111'
            }}
          >
            Get in Touch
          </h1>
          <p
            style={{
              fontSize: '1.25rem',
              lineHeight: '1.8',
              color: '#333333'
            }}
          >
            We'd love to hear from you. Whether you have a question about the book, 
            speaking engagements, or anything else, we're here to help.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-container spectrum-wash">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto'
          }}
        >
          {/* Contact Form */}
          <div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '2rem',
                fontWeight: '600',
                marginBottom: '32px',
                color: '#111111'
              }}
            >
              Send us a Message
            </h2>
            <p
              data-testid="contact-form-intro"
              style={{
                fontSize: '1rem',
                color: '#333333',
                lineHeight: '1.7',
                marginTop: '-16px',
                marginBottom: '32px'
              }}
            >
              Whether it's a general question, bulk orders, speaking, a workshop, or media, this reaches us and goes to the right place.
            </p>

            {submitted ? (
              <div
                style={{
                  padding: '32px',
                  backgroundColor: '#F4F1E8',
                  border: '2px solid #C9A24B',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  color: '#111111'
                }}
              >
                Thank you for your message! We'll respond within 2 business days.
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}
              >
                {error && (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#ffe6e6',
                      border: '1px solid #ff0000',
                      borderRadius: '4px',
                      color: '#cc0000',
                      fontSize: '0.95rem'
                    }}
                  >
                    {error}
                  </div>
                )}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      marginBottom: '8px',
                      color: '#111111'
                    }}
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      fontSize: '1rem',
                      border: '1px solid #E0E0E0',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      color: '#111111'
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      marginBottom: '8px',
                      color: '#111111'
                    }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      fontSize: '1rem',
                      border: '1px solid #E0E0E0',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      color: '#111111'
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      marginBottom: '8px',
                      color: '#111111'
                    }}
                  >
                    Reason for Contact *
                  </label>
                  <select
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value, quantity: e.target.value === 'bulk' ? formData.quantity : '' })}
                    required
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      fontSize: '1rem',
                      border: '1px solid #E0E0E0',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      color: '#111111'
                    }}
                  >
                    <option value="general">General Enquiry</option>
                    <option value="bulk">Bulk Order</option>
                    <option value="speaking">Speaking Engagement</option>
                    <option value="workshop">Workshop</option>
                    <option value="media">Media Enquiry</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      marginBottom: '8px',
                      color: '#111111'
                    }}
                  >
                    Organisation
                  </label>
                  <input
                    type="text"
                    value={formData.organisation}
                    onChange={(e) => setFormData({ ...formData, organisation: e.target.value })}
                    disabled={loading}
                    data-testid="contact-organisation-input"
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      fontSize: '1rem',
                      border: '1px solid #E0E0E0',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      color: '#111111'
                    }}
                  />
                </div>

                {formData.reason === 'bulk' && (
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        marginBottom: '8px',
                        color: '#111111'
                      }}
                    >
                      Quantity (number of copies)
                    </label>
                    <input
                      type="text"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      disabled={loading}
                      data-testid="contact-quantity-input"
                      placeholder="e.g. 50"
                      style={{
                        width: '100%',
                        padding: '14px 20px',
                        fontSize: '1rem',
                        border: '1px solid #E0E0E0',
                        borderRadius: '4px',
                        backgroundColor: '#FFFFFF',
                        color: '#111111'
                      }}
                    />
                  </div>
                )}

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      marginBottom: '8px',
                      color: '#111111'
                    }}
                  >
                    Message *
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows="6"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      fontSize: '1rem',
                      border: '1px solid #E0E0E0',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      color: '#111111',
                      resize: 'vertical',
                      fontFamily: 'var(--font-body)'
                    }}
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                  style={{ 
                    marginTop: '8px',
                    backgroundColor: loading ? '#999999' : '',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          {/* Reassurance highlight callout */}
          <div
            data-testid="contact-reassurance-card"
            style={{
              marginTop: '40px',
              padding: '28px 32px',
              backgroundColor: '#F4F1E8',
              border: '1px solid #C9A24B',
              borderRadius: '12px',
              boxShadow: '0 6px 20px rgba(154, 123, 46, 0.12)',
              textAlign: 'center'
            }}
          >
            <p
              style={{
                fontSize: '1.1rem',
                fontWeight: '500',
                color: '#5A4A1E',
                lineHeight: '1.7',
                margin: 0
              }}
            >
              We read every message and typically respond within 2 business days.
            </p>
          </div>

          {/* Follow Us */}
          <div style={{ marginTop: '48px', textAlign: 'center' }}>
            <h3
              style={{
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '18px',
                color: '#111111',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              Follow Us
            </h3>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
              <a
                href={socialLinks.linkedin || '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="contact-linkedin"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#F4F1E8',
                  color: '#111111',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#111111';
                  e.currentTarget.style.color = '#F4F1E8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#F4F1E8';
                  e.currentTarget.style.color = '#111111';
                }}
              >
                <Linkedin size={24} />
              </a>
              <a
                href={socialLinks.twitter || '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
                data-testid="contact-x"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#F4F1E8',
                  color: '#111111',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#111111';
                  e.currentTarget.style.color = '#F4F1E8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#F4F1E8';
                  e.currentTarget.style.color = '#111111';
                }}
              >
                <XIcon size={22} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
